import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Pagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LoginIcon from '@mui/icons-material/Login';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ForumIcon from '@mui/icons-material/Forum';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getPhotoUrl } from '../../utils/formatting';
import AdminTermsTab from '../components/AdminTermsTab';
import {
  SystemMessageTemplate,
  SystemCampaignTemplate,
  SetupResult,
  CampaignDef,
  fetchSystemMessageTemplates,
  createSystemMessageTemplate,
  updateSystemMessageTemplate,
  deleteSystemMessageTemplate,
  fetchSystemCampaignTemplates,
  createSystemCampaignTemplate,
  updateSystemCampaignTemplate,
  deleteSystemCampaignTemplate,
  setupCeaFromSystemTemplates,
  fetchCampaignDefs,
  createCampaignDef,
  updateCampaignDef,
  deleteCampaignDef,
  fetchAdminAccessCodeQuotas,
  upsertAdminAccessCodeQuota,
  deleteAdminAccessCodeQuota,
  fetchAdminAccessCodes,
  AccessCodeQuota,
  AccessCodeWithNames,
  fetchAdminUsers,
  AdminUserRow,
  adminImportEcbClients,
  ImportEcbContact,
  fetchAdminUserEngagement,
  EngagementTemplateRow,
  fetchAdminAppointments,
  AdminAppointment,
  updateAppointment,
  createAppointment,
  fetchAdminAgentProfile,
  sendPaymentRequest,
  impersonateUser,
  fetchAdminAuditLogs,
  AdminAuditLogRow,
  fetchAdminWhatsAppRestriction,
  WhatsAppRestriction,
  fetchAdminCheckInDraft,
  logAdminCheckIn,
  AdminCheckInSent,
  saveAdminUserRemarks,
} from '../services/api';

const TEMPLATE_TYPES = ['intro', 'match', 'viewing', 'marketing'];
const TEMPLATE_TYPE_LABELS: Record<string, string> = {
  intro: 'Intro',
  match: 'Match',
  viewing: 'Viewing',
  marketing: 'Marketing',
};
const CHANNELS = ['whatsapp', 'email'];
const CHANNEL_LABELS: Record<string, string> = { whatsapp: 'WhatsApp', email: 'Email' };
const PLACEHOLDERS = ['{{client_name}}', '{{property_address}}', '{{project_link}}', '{{agent_phone}}', '{{agent_name}}'];

const ORANGE = '#ed8936';

function formatPaymentDate(value: string | null): string {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return new Intl.DateTimeFormat('en-SG', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Singapore',
  }).format(date);
}

export default function Admin() {
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const notify = useCallback((msg: string, severity: 'success' | 'error' = 'success') => {
    setToast({ msg, severity });
  }, []);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        Admin · System Templates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Maintain the org-wide master templates, then set up an agent by copying them into their account.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 3 }}>
        <Tab label="Message Templates" sx={{ textTransform: 'none' }} />
        <Tab label="Campaign Template" sx={{ textTransform: 'none' }} />
        <Tab label="Custom Campaigns" sx={{ textTransform: 'none' }} />
        <Tab label="Terms & Conditions" sx={{ textTransform: 'none' }} />
        <Tab label="Manage Users" sx={{ textTransform: 'none' }} />
        <Tab label="Access Code Quotas" sx={{ textTransform: 'none' }} />
        <Tab label="All Access Codes" sx={{ textTransform: 'none' }} />
      </Tabs>

      {tab === 0 && <MessageTemplatesTab notify={notify} />}
      {tab === 1 && <CampaignTemplatesTab notify={notify} />}
      {tab === 2 && <CustomCampaignsTab notify={notify} />}
      {tab === 3 && <AdminTermsTab notify={notify} />}
      {tab === 4 && <ManageUsersTab notify={notify} />}
      {tab === 5 && <AccessCodeQuotasTab notify={notify} />}
      {tab === 6 && <AllAccessCodesTab />}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Message Templates tab
// ─────────────────────────────────────────────────────────────────────────
function MessageTemplatesTab({ notify }: { notify: (m: string, s?: 'success' | 'error') => void }) {
  const [rows, setRows] = useState<SystemMessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SystemMessageTemplate | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState('intro');
  const [channel, setChannel] = useState('whatsapp');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchSystemMessageTemplates());
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setTitle(''); setBody(''); setType('intro'); setChannel('whatsapp');
    setDialogOpen(true);
  };
  const openEdit = (r: SystemMessageTemplate) => {
    setEditing(r);
    setTitle(r.Title); setBody(r.Body); setType(r.Type); setChannel(r.Channel);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await updateSystemMessageTemplate(editing.Id, { Title: title, Body: body, Type: type, Channel: channel });
        notify('Template updated (previous version archived)');
      } else {
        await createSystemMessageTemplate({ Title: title, Body: body, Type: type, Channel: channel });
        notify('Template created');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: SystemMessageTemplate) => {
    if (!window.confirm(`Delete system template "${r.Title}"? Agents already set up keep their copy.`)) return;
    try {
      await deleteSystemMessageTemplate(r.Id);
      notify('Template deleted');
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to delete', 'error');
    }
  };

  const insertPlaceholder = (ph: string) => setBody((b) => `${b}${ph}`);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {rows.length} active message template{rows.length === 1 ? '' : 's'}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}>
          New Template
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <EmptyState text="No system message templates yet. Create one to get started." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {rows.map((r) => (
            <Paper key={r.Id} variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={600}>{r.Title}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <Chip label={TEMPLATE_TYPE_LABELS[r.Type] || r.Type} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                    <Chip label={CHANNEL_LABELS[r.Channel] || r.Channel} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                    {r.Body}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit System Template' : 'New System Template'}</DialogTitle>
        <DialogContent>
          {editing && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Saving archives the current version and creates a new one (edit history is kept).
            </Alert>
          )}
          <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={type} label="Type" onChange={(e) => setType(e.target.value)}>
                {TEMPLATE_TYPES.map((t) => (<MenuItem key={t} value={t}>{TEMPLATE_TYPE_LABELS[t]}</MenuItem>))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select value={channel} label="Channel" onChange={(e) => setChannel(e.target.value)}>
                {CHANNELS.map((c) => (<MenuItem key={c} value={c}>{CHANNEL_LABELS[c]}</MenuItem>))}
              </Select>
            </FormControl>
          </Box>
          <TextField fullWidth multiline rows={5} label="Body" value={body} onChange={(e) => setBody(e.target.value)} sx={{ mb: 1 }} />
          <Typography variant="caption" sx={{ color: ORANGE, mb: 0.5, display: 'block', fontWeight: 700 }}>INSERT PLACEHOLDERS</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {PLACEHOLDERS.map((ph) => (
              <Chip key={ph} label={ph.replace(/\{\{|\}\}/g, '#')} size="small" onClick={() => insertPlaceholder(ph)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(237, 137, 54, 0.15)' } }} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!title.trim() || !body.trim() || saving}
            sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Campaign Templates tab
// ─────────────────────────────────────────────────────────────────────────
function CampaignTemplatesTab({ notify }: { notify: (m: string, s?: 'success' | 'error') => void }) {
  const [grouped, setGrouped] = useState<Record<string, SystemCampaignTemplate[]>>({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SystemCampaignTemplate | null>(null);
  const [name, setName] = useState('');
  const [dayNumber, setDayNumber] = useState(1);
  const [channel, setChannel] = useState('whatsapp');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSystemCampaignTemplates();
      setGrouped(data.grouped || {});
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const names = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  const openNew = () => {
    setEditing(null);
    setName(names[0] || 'Welcome Series'); setDayNumber(1); setChannel('whatsapp');
    setMessage('');
    setDialogOpen(true);
  };
  const openEdit = (r: SystemCampaignTemplate) => {
    setEditing(r);
    setName(r.Name); setDayNumber(r.DayNumber); setChannel(r.Channel);
    setMessage(r.MessageTemplate);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      // CTA label/URL were retired with the Day 7 rework — saving always clears
      // any legacy values so re-edited days lose their old CTA.
      const payload = {
        Name: name.trim(),
        DayNumber: dayNumber,
        Channel: channel,
        MessageTemplate: message,
        CtaLabel: null,
        CtaUrl: null,
      };
      if (editing) {
        await updateSystemCampaignTemplate(editing.Id, payload);
        notify('Campaign day updated (previous version archived)');
      } else {
        await createSystemCampaignTemplate(payload);
        notify('Campaign day created');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: SystemCampaignTemplate) => {
    if (!window.confirm(`Delete "${r.Name}" · Day ${r.DayNumber}? Agents already set up keep their copy.`)) return;
    try {
      await deleteSystemCampaignTemplate(r.Id);
      notify('Campaign day deleted');
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to delete', 'error');
    }
  };

  const insertPlaceholder = (ph: string) => setMessage((m) => `${m}${ph}`);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {names.length} campaign{names.length === 1 ? '' : 's'}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}>
          New Campaign Day
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : names.length === 0 ? (
        <EmptyState text="No system campaign templates yet. Add a campaign day to get started." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {names.map((campName) => (
            <Box key={campName}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>{campName}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {grouped[campName].slice().sort((a, b) => a.DayNumber - b.DayNumber).map((r) => (
                  <Paper key={r.Id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                          <Chip label={`Day ${r.DayNumber}`} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(237, 137, 54, 0.15)', color: ORANGE, fontWeight: 700 }} />
                          <Chip label={CHANNEL_LABELS[r.Channel] || r.Channel} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {r.MessageTemplate || <em>(no message)</em>}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Campaign Day' : 'New Campaign Day'}</DialogTitle>
        <DialogContent>
          {editing && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Saving archives the current version and creates a new one (edit history is kept).
            </Alert>
          )}
          <TextField fullWidth label="Campaign Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField type="number" label="Day Number" value={dayNumber}
              onChange={(e) => setDayNumber(Math.max(1, Number(e.target.value) || 1))}
              sx={{ width: 160 }} inputProps={{ min: 1 }} />
            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select value={channel} label="Channel" onChange={(e) => setChannel(e.target.value)}>
                {CHANNELS.map((c) => (<MenuItem key={c} value={c}>{CHANNEL_LABELS[c]}</MenuItem>))}
              </Select>
            </FormControl>
          </Box>
          <TextField fullWidth multiline rows={5} label="Message" value={message}
            onChange={(e) => setMessage(e.target.value)} placeholder="Hi {{client_name}}! ..." sx={{ mb: 1 }} />
          <Typography variant="caption" sx={{ color: ORANGE, mb: 0.5, display: 'block', fontWeight: 700 }}>INSERT PLACEHOLDERS</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {PLACEHOLDERS.map((ph) => (
              <Chip key={ph} label={ph.replace(/\{\{|\}\}/g, '#')} size="small" onClick={() => insertPlaceholder(ph)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(237, 137, 54, 0.15)' } }} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!name.trim() || saving}
            sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Custom Campaigns tab — admin-authored campaigns granted to agents by CEA
// ─────────────────────────────────────────────────────────────────────────
function CustomCampaignsTab({ notify }: { notify: (m: string, s?: 'success' | 'error') => void }) {
  const [rows, setRows] = useState<CampaignDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CampaignDef | null>(null);
  const [name, setName] = useState('');
  const [numDays, setNumDays] = useState(3);
  const [ceaNumber, setCeaNumber] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchCampaignDefs());
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setName(''); setNumDays(3); setCeaNumber('');
    setDialogOpen(true);
  };
  const openEdit = (r: CampaignDef) => {
    setEditing(r);
    setName(r.Name); setNumDays(r.NumDays); setCeaNumber(r.ceaNumber || '');
    setDialogOpen(true);
  };

  const save = async () => {
    if (!name.trim()) return;
    const assigned = ceaNumber.trim() || null;
    setSaving(true);
    try {
      if (editing) {
        await updateCampaignDef(editing.Id, { Name: name.trim(), NumDays: numDays, ceaNumber: assigned });
        notify('Campaign updated');
      } else {
        await createCampaignDef({ Name: name.trim(), NumDays: numDays, ceaNumber: assigned });
        notify('Campaign created');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r: CampaignDef) => {
    try {
      await updateCampaignDef(r.Id, { IsActive: !r.IsActive });
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to update', 'error');
    }
  };

  const remove = async (r: CampaignDef) => {
    if (!window.confirm(`Delete campaign "${r.Name}"? Agents lose access; their configured days remain but stop sending.`)) return;
    try {
      await deleteCampaignDef(r.Id);
      notify('Campaign deleted');
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to delete', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {rows.length} custom campaign{rows.length === 1 ? '' : 's'}. The assigned agent configures each day in Campaigns → Custom Campaigns.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}>
          New Campaign
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <EmptyState text="No custom campaigns yet. Create one and assign it to one agent by CEA number." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {rows.map((r) => (
            <Paper key={r.Id} variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
                    <Typography fontWeight={600}>{r.Name}</Typography>
                    <Chip label={`${r.NumDays} day${r.NumDays === 1 ? '' : 's'}`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                    <Chip
                      label={r.IsActive ? 'Active' : 'Paused'}
                      size="small"
                      onClick={() => toggleActive(r)}
                      sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer',
                        bgcolor: r.IsActive ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.15)',
                        color: r.IsActive ? '#22c55e' : '#94a3b8', fontWeight: 700 }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Access: {r.ceaNumber ? r.ceaNumber : <em>no agent yet</em>}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Campaign Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <TextField type="number" label="Number of days" value={numDays}
            onChange={(e) => setNumDays(Math.min(60, Math.max(1, Number(e.target.value) || 1)))}
            sx={{ width: 200, mb: 2 }} inputProps={{ min: 1, max: 60 }} />
          <Typography variant="caption" sx={{ color: ORANGE, mb: 0.5, display: 'block', fontWeight: 700 }}>
            ASSIGNED AGENT (CEA NUMBER)
          </Typography>
          <TextField
            fullWidth
            value={ceaNumber}
            onChange={(e) => setCeaNumber(e.target.value)}
            placeholder="One CEA number, e.g. R012345A"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!name.trim() || saving}
            sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Set up a User tab
// ─────────────────────────────────────────────────────────────────────────
// Reusable "Set up an agent" dialog — copies the system templates/campaign into
// an agent's account. Used from the Manage Users header and the Appointments tab.
function SetupAgentDialog({
  open, onClose, notify, initialCea,
}: {
  open: boolean;
  onClose: () => void;
  notify: (m: string, s?: 'success' | 'error') => void;
  initialCea?: string;
}) {
  const [cea, setCea] = useState(initialCea || '');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SetupResult | null>(null);

  useEffect(() => {
    if (open) { setCea(initialCea || ''); setResult(null); }
  }, [open, initialCea]);

  const run = async () => {
    const trimmed = cea.trim();
    if (!trimmed) return;
    if (!window.confirm(
      `Set up ${trimmed} from the system templates?\n\nThis REPLACES the agent's current message templates and campaign with fresh copies from the system templates.`
    )) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await setupCeaFromSystemTemplates(trimmed);
      setResult(res);
      notify(`${trimmed} set up successfully`);
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Set-up failed', 'error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketLaunchIcon sx={{ color: ORANGE }} />
          <span>Set up an agent</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the agent's CEA number and click Set up. The system copies the current
          message templates and campaign into the agent's account, replacing what's there now.
        </Typography>
        <TextField
          fullWidth
          label="CEA Number"
          value={cea}
          onChange={(e) => setCea(e.target.value)}
          placeholder="e.g. R012345A"
          onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
          disabled={running}
          autoFocus
        />
        {result && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={700}>{result.cea} set up</Typography>
            <Typography variant="body2">• {result.messagesCopied} message template(s) copied ({result.messagesReplaced} replaced)</Typography>
            <Typography variant="body2">• {result.campaignsCopied} campaign day(s) copied</Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running}>Close</Button>
        <Button
          variant="contained"
          onClick={run}
          disabled={!cea.trim() || running}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
        >
          {running ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Set up'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Manage Users tab — activated users + register-interest appointments
// ─────────────────────────────────────────────────────────────────────────
function ManageUsersTab({ notify }: { notify: (m: string, s?: 'success' | 'error') => void }) {
  const [subTab, setSubTab] = useState(0);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupCea, setSetupCea] = useState('');

  const openSetup = (cea?: string) => { setSetupCea(cea || ''); setSetupOpen(true); };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>Manage Users</Typography>
        <Button
          variant="contained"
          startIcon={<RocketLaunchIcon />}
          onClick={() => openSetup()}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
        >
          Set up a user
        </Button>
      </Box>

      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2 }}>
        <Tab label="Current users" sx={{ textTransform: 'none' }} />
        <Tab label="Appointments" sx={{ textTransform: 'none' }} />
        <Tab label="Admin activity" sx={{ textTransform: 'none' }} />
      </Tabs>

      {subTab === 0 && <CurrentUsersPanel notify={notify} />}
      {subTab === 1 && <AppointmentsPanel notify={notify} onSetupUser={openSetup} />}
      {subTab === 2 && <AdminActivityPanel notify={notify} />}

      <SetupAgentDialog
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        notify={notify}
        initialCea={setupCea}
      />
    </Box>
  );
}

function StatusChip({ ok, label }: { ok: boolean; label: string }) {
  return (
    <Chip
      size="small"
      label={label}
      color={ok ? 'success' : 'default'}
      variant={ok ? 'filled' : 'outlined'}
      sx={{ mr: 0.5 }}
    />
  );
}

function CurrentUsersPanel({ notify }: { notify: (m: string, s?: 'success' | 'error') => void }) {
  const [rows, setRows] = useState<AdminUserRow[] | null>(null);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState<string | null>(null);
  const [engagementUser, setEngagementUser] = useState<{ cea: string; name: string } | null>(null);
  // "Login as user": confirm target, then swap this session for theirs.
  const [loginAsTarget, setLoginAsTarget] = useState<AdminUserRow | null>(null);
  const [loginAsBusy, setLoginAsBusy] = useState(false);
  // Paste-import ECB clients for the agent whose row was clicked.
  const [importTarget, setImportTarget] = useState<AdminUserRow | null>(null);
  // Live WhatsApp restriction check / post-setup check-in, per agent row.
  const [healthTarget, setHealthTarget] = useState<AdminUserRow | null>(null);
  const [checkInTarget, setCheckInTarget] = useState<AdminUserRow | null>(null);

  const runLoginAs = async () => {
    if (!loginAsTarget) return;
    setLoginAsBusy(true);
    try {
      await impersonateUser(loginAsTarget.cea);
      // Full reload so every view re-fetches under the impersonated session.
      window.location.assign('/super-connector');
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to log in as user', 'error');
      setLoginAsBusy(false);
    }
  };

  const load = useCallback(() => {
    fetchAdminUsers().then(setRows).catch(() => notify('Failed to load users', 'error'));
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  // Save on blur, and only when the text actually changed — mirrors the
  // appointment remarks. `rows` is patched in place so a later reload (or a
  // re-render from another action) keeps what was typed.
  const saveRemarks = async (cea: string, remarks: string) => {
    const current = rows?.find((r) => r.cea === cea)?.remarks || '';
    if (remarks === current) return;
    try {
      await saveAdminUserRemarks(cea, remarks);
      setRows((prev) => prev?.map((r) => (r.cea === cea ? { ...r, remarks: remarks || null } : r)) || prev);
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to save remarks', 'error');
    }
  };

  const sendPayment = async (cea: string) => {
    setSending(cea);
    try {
      const res = await sendPaymentRequest(cea);
      notify(res.emailSentTo ? `Payment request emailed to ${res.emailSentTo}` : 'Payment request created (no email on file)');
      load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to send payment request', 'error');
    } finally {
      setSending(null);
    }
  };

  if (!rows) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (rows.length === 0) return <EmptyState text="No activated users yet." />;

  const normalizedSearch = search.trim().toLowerCase();
  const filteredRows = normalizedSearch
    ? rows.filter((u) => (
      u.name.toLowerCase().includes(normalizedSearch)
      || u.cea.toLowerCase().includes(normalizedSearch)
      || (u.mobile || '').toLowerCase().includes(normalizedSearch)
    ))
    : rows;

  return (
    <>
    <TextField
      value={search}
      onChange={(event) => setSearch(event.target.value)}
      placeholder="Search by name, CEA or mobile"
      size="small"
      fullWidth
      inputProps={{ 'aria-label': 'Search users by name, CEA or mobile' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
      sx={{ mb: 2, maxWidth: 420 }}
    />
    {/* overflowX + minWidth: on narrow screens the table scrolls sideways
        instead of crushing its columns. */}
    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 1500 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>CEA</TableCell>
            <TableCell>Mobile</TableCell>
            <TableCell align="center">Clients</TableCell>
            <TableCell>Setup status</TableCell>
            <TableCell>7-Day Engagement</TableCell>
            <TableCell>Payment</TableCell>
            <TableCell>Remarks</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRows.map((u) => (
            <TableRow key={u.cea} hover>
              <TableCell>
                {u.name}
                {u.role?.toUpperCase() === 'ADMIN' && <Chip size="small" label="Admin" sx={{ ml: 1 }} />}
              </TableCell>
              <TableCell>{u.cea}</TableCell>
              <TableCell>{u.mobile || '—'}</TableCell>
              <TableCell align="center">
                <Chip size="small" label={u.clientCount} variant="outlined" />
              </TableCell>
              <TableCell>
                <StatusChip ok={u.whatsappStatus === 'connected'} label="WhatsApp" />
                <StatusChip ok={u.calendlyConnected} label="Calendly" />
                <StatusChip ok={u.engagementActive} label="7-Day" />
              </TableCell>
              <TableCell>
                <Button size="small" variant="text" onClick={() => setEngagementUser({ cea: u.cea, name: u.name })} sx={{ textTransform: 'none' }}>
                  View template
                </Button>
              </TableCell>
              <TableCell>
                {u.paymentStatus === 'paid' ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75, py: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap' }}>
                      <Chip size="small" color="success" label="Paid" />
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {formatPaymentDate(u.paymentPaidAt)}
                      </Typography>
                    </Box>
                    {u.paymentReceiptUrl ? (
                      <Button
                        component="a"
                        href={u.paymentReceiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="outlined"
                        startIcon={<ReceiptLongIcon />}
                        sx={{
                          textTransform: 'none',
                          minWidth: 0,
                          px: 1,
                          py: 0.25,
                          lineHeight: 1.4,
                          borderColor: 'divider',
                        }}
                      >
                        View receipt
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {u.paymentReceiptStatus === 'ERROR' ? 'Receipt unavailable' : 'Receipt processing'}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Button
                    size="small"
                    variant={u.paymentStatus === 'pending' ? 'outlined' : 'contained'}
                    onClick={() => sendPayment(u.cea)}
                    disabled={sending === u.cea}
                    sx={u.paymentStatus === 'pending' ? undefined : { bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
                  >
                    {sending === u.cea
                      ? <CircularProgress size={16} />
                      : u.paymentStatus === 'pending' ? 'Resend payment' : 'Send payment'}
                  </Button>
                )}
              </TableCell>
              <TableCell>
                {/* Uncontrolled + save-on-blur, like the appointment remarks:
                    typing never re-renders the whole table. */}
                <TextField
                  size="small"
                  variant="standard"
                  placeholder="Add remarks…"
                  multiline
                  maxRows={4}
                  defaultValue={u.remarks || ''}
                  onBlur={(e) => saveRemarks(u.cea, e.target.value)}
                  sx={{ minWidth: 160 }}
                  inputProps={{ 'aria-label': `Remarks for ${u.name}` }}
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                  <Tooltip title={`Paste-import ECB clients for ${u.name}`}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<GroupAddIcon />}
                      onClick={() => setImportTarget(u)}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      Import clients
                    </Button>
                  </Tooltip>
                  <Tooltip title={`Ask WhatsApp whether ${u.name}'s account is restricted from sending`}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<HealthAndSafetyIcon />}
                      onClick={() => setHealthTarget(u)}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      WhatsApp health
                    </Button>
                  </Tooltip>
                  <Tooltip title={`WhatsApp ${u.name} to check everything works after setup`}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<ForumIcon />}
                      onClick={() => setCheckInTarget(u)}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      Check-in
                    </Button>
                  </Tooltip>
                  <Tooltip title={`Log in as ${u.name} to help manage their account`}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<LoginIcon />}
                      onClick={() => setLoginAsTarget(u)}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      Login as
                    </Button>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {filteredRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                No users match “{search.trim()}”.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
    <EngagementTemplateDialog
      open={!!engagementUser}
      cea={engagementUser?.cea || ''}
      name={engagementUser?.name || ''}
      onClose={() => setEngagementUser(null)}
      notify={notify}
    />
    <ImportEcbClientsDialog
      user={importTarget}
      onClose={() => setImportTarget(null)}
      onImported={load}
      notify={notify}
    />
    <WhatsAppHealthDialog user={healthTarget} onClose={() => setHealthTarget(null)} />
    <CheckInDialog user={checkInTarget} onClose={() => setCheckInTarget(null)} notify={notify} />
    <Dialog
      open={!!loginAsTarget}
      onClose={() => !loginAsBusy && setLoginAsTarget(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle fontWeight={700}>Log in as {loginAsTarget?.name}?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          You will see and manage Super Connector exactly as {loginAsTarget?.name} ({loginAsTarget?.cea}) does.
          Every change you make while logged in as them is recorded in the admin activity log.
          Use the purple banner at the bottom of the page to return to your own account.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setLoginAsTarget(null)} disabled={loginAsBusy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={loginAsBusy}
          onClick={runLoginAs}
          startIcon={<LoginIcon />}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
        >
          {loginAsBusy ? 'Switching...' : 'Log in as user'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

function formatRestrictionUntil(value: string | null | undefined): string {
  if (!value) return 'an unspecified date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'an unspecified date';
  return new Intl.DateTimeFormat('en-SG', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZone: 'Asia/Singapore',
  }).format(date);
}

// Asks WhatsApp (via the bridge) whether this agent's account is currently
// blocked from starting new chats. WhatsApp applies this brake silently: sends
// look accepted but never arrive, so this is the only way to confirm it.
function WhatsAppHealthDialog({ user, onClose }: { user: AdminUserRow | null; onClose: () => void }) {
  const [state, setState] = useState<WhatsAppRestriction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const check = useCallback((cea: string) => {
    setBusy(true);
    setError(null);
    fetchAdminWhatsAppRestriction(cea)
      .then(setState)
      .catch((e) => setError(e instanceof Error ? e.message : 'Check failed'))
      .finally(() => setBusy(false));
  }, []);

  useEffect(() => { if (user) check(user.cea); }, [user, check]);

  return (
    <Dialog
      open={!!user}
      onClose={onClose}
      TransitionProps={{ onExited: () => { setState(null); setError(null); } }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle fontWeight={700}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HealthAndSafetyIcon sx={{ color: ORANGE }} />
          <span>WhatsApp health — {user?.name}</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        {busy && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">Asking WhatsApp…</Typography>
          </Box>
        )}

        {!busy && error && <Alert severity="error">{error}</Alert>}

        {!busy && !error && state && !state.connected && (
          <Alert severity="warning">
            <Typography variant="body2" fontWeight={700}>WhatsApp is not connected</Typography>
            <Typography variant="body2">
              Their session is “{state.whatsappStatus || 'disconnected'}”, so WhatsApp can’t be asked about
              restrictions. Get them to re-pair WhatsApp in Settings, then check again.
            </Typography>
          </Alert>
        )}

        {!busy && !error && state?.connected && state.restricted && (
          <Alert severity="error">
            <Typography variant="body2" fontWeight={700}>
              Restricted until {formatRestrictionUntil(state.until)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              WhatsApp is blocking this account from starting new chats
              {state.enforcementType ? ` (${state.enforcementType})` : ''}. Messages to contacts who have not
              messaged them first will be rejected until the restriction lifts.
            </Typography>
            {state.hint && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>{state.hint}</Typography>
            )}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Pause campaigns from this agent until then. Replies to active conversations still work.
            </Typography>
          </Alert>
        )}

        {!busy && !error && state?.connected && !state.restricted && (
          <Alert severity="success">
            <Typography variant="body2" fontWeight={700}>No restriction — sending normally</Typography>
            <Typography variant="body2">
              WhatsApp is not throttling this account. Cold outreach still consumes their “new chat” budget,
              so ramp campaigns up gradually rather than blasting a large list at once.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          disabled={busy || !user}
          onClick={() => user && check(user.cea)}
          sx={{ textTransform: 'none' }}
        >
          Check again
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Audit timestamps are stored UTC — render them in SGT, as Admin activity does. */
function formatCheckInSentAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-SG', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    hour12: true, timeZone: 'Asia/Singapore',
  }).format(date);
}

// Post-setup check-in: hands the admin a WhatsApp link (same approach as the
// appointment reminder) pre-filled with the message, so it goes out from their
// own WhatsApp and arrives as a message from a colleague the agent knows. The
// default wording is composed server-side and shown here for editing first.
function CheckInDialog({
  user, onClose, notify,
}: {
  user: AdminUserRow | null;
  onClose: () => void;
  notify: (m: string, s?: 'success' | 'error') => void;
}) {
  const [draft, setDraft] = useState<
    { mobile: string; whatsappPhone: string; message: string; history: AdminCheckInSent[] } | null
  >(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    setError(null);
    fetchAdminCheckInDraft(user.cea)
      .then((d) => {
        setDraft({
          mobile: d.mobile,
          whatsappPhone: d.whatsappPhone,
          message: d.message,
          history: d.history || [],
        });
        setMessage(d.message);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to prepare check-in'))
      .finally(() => setLoading(false));
  }, [user]);

  // WhatsApp carries the edited text, so the link is rebuilt as the admin types.
  const whatsappUrl = draft && message.trim()
    ? `https://api.whatsapp.com/send/?phone=${draft.whatsappPhone}&text=${encodeURIComponent(message.trim())}`
    : '';

  const openWhatsApp = () => {
    if (!user) return;
    // The audit trail shouldn't block opening WhatsApp — log it best-effort.
    logAdminCheckIn(user.cea).catch((e) => console.warn('[check-in] audit log failed', e));
    notify(`WhatsApp opened for ${user.name}${draft ? ` (${draft.mobile})` : ''}`);
    onClose();
  };

  return (
    <Dialog
      open={!!user}
      onClose={onClose}
      TransitionProps={{ onExited: () => { setDraft(null); setMessage(''); setError(null); } }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle fontWeight={700}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          <span>Check in with {user?.name}</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">Preparing message…</Typography>
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && draft && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Opens WhatsApp with this message ready to send to <strong>{draft.mobile}</strong> from your own
              account — you still press send there. Edit the message below if you want (WhatsApp formatting
              works: *bold*, _italic_).
            </Typography>

            {/* Sent-before history, so nobody checks in with the same agent twice. */}
            {draft.history.length > 0 ? (
              <Alert severity="warning" icon={<HistoryIcon fontSize="small" />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Check-in already sent {draft.history.length === 1 ? 'once' : `${draft.history.length} times`}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {draft.history.map((h, i) => (
                    <Box component="li" key={`${h.sentAt}-${h.adminCea}-${i}`}>
                      <Typography variant="caption">
                        {formatCheckInSentAt(h.sentAt)} — by {h.adminName}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Alert>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                No check-in sent to this agent yet.
              </Typography>
            )}
            <TextField
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={10}
              fullWidth
              inputProps={{ 'aria-label': 'Check-in message' }}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              Send these a few at a time. Each message to someone who hasn’t messaged you recently counts
              against your own account’s “new chat” budget, and WhatsApp temporarily blocks accounts that
              reach out to too many people at once.
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          component="a"
          href={whatsappUrl || undefined}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<WhatsAppIcon />}
          disabled={loading || !!error || !whatsappUrl}
          onClick={openWhatsApp}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
        >
          Open WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Paste-import ECB clients for the agent whose row launched the dialog: paste
// rows copied from a spreadsheet (tab-separated: Company Name / name / mobile —
// a 2-column name/mobile paste also works) and submit. The server creates every
// parsed row as a client under the agent (duplicates included), tags them "ECB",
// and creates the agent's four standard tags if missing.
function ImportEcbClientsDialog({
  user, onClose, onImported, notify,
}: {
  user: AdminUserRow | null;
  onClose: () => void;
  onImported: () => void;
  notify: (m: string, s?: 'success' | 'error') => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  // Parse tab-separated lines. 3+ columns → company/name/mobile; 2 → name/mobile.
  // A header row (any cell reading "mobile") is skipped. Comma is NOT used as a
  // separator — client names legitimately contain commas ("TAN, BENJAMIN").
  const parsed = useMemo(() => {
    const contacts: ImportEcbContact[] = [];
    let invalid = 0;
    for (const rawLine of text.split(/\r?\n/)) {
      const line = rawLine.trim();
      if (!line) continue;
      const cells = line.split('\t').map((c) => c.trim());
      if (cells.some((c) => c.toLowerCase() === 'mobile')) continue; // header row
      const [companyName, name, mobile] =
        cells.length >= 3 ? [cells[0], cells[1], cells[2]] : ['', cells[0] || '', cells[1] || ''];
      const digits = mobile.replace(/\D/g, '');
      if (!name || digits.length < 8) { invalid++; continue; }
      contacts.push({ companyName: companyName || undefined, name, mobile });
    }
    return { contacts, invalid };
  }, [text]);

  const submit = async () => {
    if (!user || parsed.contacts.length === 0) return;
    setBusy(true);
    try {
      const res = await adminImportEcbClients(user.cea, parsed.contacts);
      notify(
        `Created ${res.created} client(s) under ${user.name} (${user.cea})`
        + (res.tagsCreated ? `, ${res.tagsCreated} tag(s) added` : '')
        + (res.skipped ? `, ${res.skipped} row(s) skipped` : ''),
      );
      onImported();
      setText('');
      onClose();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to import clients', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!user}
      onClose={() => !busy && onClose()}
      // Clear the paste only after the close animation, and never leak one
      // agent's paste into another agent's dialog.
      TransitionProps={{ onExited: () => setText('') }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle fontWeight={700}>
        Import ECB clients — {user?.name} ({user?.cea})
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste rows copied from your spreadsheet (columns: Company Name, name,
          mobile). Every row is created as a new client under this agent —
          duplicates are not skipped — tagged <b>ECB</b>, with 7-Day Engagement
          off. The agent's four standard tags (ECB, EOI, FB Leads, PG Leads) are
          created if missing.
        </Typography>
        <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={8}
          maxRows={16}
          fullWidth
          placeholder={'Company Name\tname\tmobile\nERA REALTY NETWORK PTE LTD\tALOYSIUS PNG WEI KEAT\t6582678078'}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            color={parsed.contacts.length > 0 ? 'success' : 'default'}
            variant={parsed.contacts.length > 0 ? 'filled' : 'outlined'}
            label={`${parsed.contacts.length} client(s) parsed`}
          />
          {parsed.invalid > 0 && (
            <Chip size="small" color="warning" label={`${parsed.invalid} line(s) ignored (missing name/mobile)`} />
          )}
        </Box>
        {parsed.contacts.length > 0 && (
          <Paper variant="outlined" sx={{ mt: 1.5, maxHeight: 180, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Company</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parsed.contacts.map((c, i) => (
                  <TableRow key={`${c.mobile}-${i}`}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.mobile}</TableCell>
                    <TableCell>{c.companyName || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !user || parsed.contacts.length === 0}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <GroupAddIcon />}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
        >
          {busy ? 'Importing…' : `Create ${parsed.contacts.length || ''} client(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Admin-only view of the audit trail: impersonation sessions and every change
// made while logged in as a user. Agents can never load this (endpoint is
// admin-gated), so nothing here leaks to clients.
function AdminActivityPanel({ notify }: { notify: (m: string, s?: 'success' | 'error') => void }) {
  const PAGE_SIZE = 25;
  const [data, setData] = useState<{ total: number; logs: AdminAuditLogRow[] } | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchAdminAuditLogs({ limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then(setData)
      .catch(() => notify('Failed to load admin activity', 'error'));
  }, [page, notify]);

  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (data.total === 0) return <EmptyState text="No admin activity recorded yet. Actions appear here when an admin logs in as a user." />;

  const actionChip = (action: string) => {
    switch (action) {
      case 'IMPERSONATE_START':
        return <Chip size="small" label="Logged in as user" sx={{ bgcolor: '#3b82f618', color: 'text.primary', border: '1px solid #3b82f655', fontWeight: 600 }} />;
      case 'IMPERSONATE_END':
        return <Chip size="small" label="Returned to admin" sx={{ bgcolor: '#15803d18', color: 'text.primary', border: '1px solid #15803d55', fontWeight: 600 }} />;
      case 'IMPERSONATED_REQUEST':
        return <Chip size="small" label="Change as user" sx={{ bgcolor: '#b4530918', color: 'text.primary', border: '1px solid #b4530955', fontWeight: 600 }} />;
      case 'IMPORT_CLIENTS':
        return <Chip size="small" label="Imported clients" sx={{ bgcolor: '#38a16918', color: 'text.primary', border: '1px solid #38a16955', fontWeight: 600 }} />;
      case 'SEND_CHECKIN':
        return <Chip size="small" label="Sent check-in" sx={{ bgcolor: '#25D36618', color: 'text.primary', border: '1px solid #25D36655', fontWeight: 600 }} />;
      default:
        return <Chip size="small" label={action} variant="outlined" />;
    }
  };

  const formatWhen = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-SG', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Singapore',
    }).format(date);
  };

  const pageCount = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>When</TableCell>
              <TableCell>Admin</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.logs.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatWhen(log.createdAt)}</TableCell>
                <TableCell>{log.adminName || log.adminCea}</TableCell>
                <TableCell>{actionChip(log.action)}</TableCell>
                <TableCell>{log.targetName || log.targetCea || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 420, overflowWrap: 'anywhere', color: 'text.secondary' }}>
                  {log.details || ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={pageCount} page={page + 1} onChange={(_, p) => setPage(p - 1)} shape="rounded" />
        </Box>
      )}
    </>
  );
}

// Read-only viewer for an agent's 7-Day Engagement template.
function EngagementTemplateDialog({
  open, cea, name, onClose, notify,
}: {
  open: boolean;
  cea: string;
  name: string;
  onClose: () => void;
  notify: (m: string, s?: 'success' | 'error') => void;
}) {
  const [rows, setRows] = useState<EngagementTemplateRow[] | null>(null);

  useEffect(() => {
    if (!open || !cea) { setRows(null); return; }
    setRows(null);
    fetchAdminUserEngagement(cea)
      .then(setRows)
      .catch(() => notify('Failed to load engagement template', 'error'));
  }, [open, cea, notify]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>7-Day Engagement · {name || cea}</DialogTitle>
      <DialogContent dividers>
        {!rows ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No 7-Day Engagement template set up for this agent yet.
          </Typography>
        ) : (
          rows.map((r) => (
            <Paper key={r.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip size="small" label={`Day ${r.dayNumber}`} sx={{ bgcolor: ORANGE, color: '#fff' }} />
                <Chip size="small" label={r.channel} variant="outlined" />
                {!r.isActive && <Chip size="small" label="Inactive" color="default" variant="outlined" />}
              </Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: (r.mediaUrl || r.imageUrl || r.voiceUrl || r.ctaLabel) ? 1 : 0 }}>
                {r.messageTemplate || <em>(no message)</em>}
              </Typography>
              {(r.imageUrl || r.mediaUrl) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  🖼️ Image: {r.imageUrl || r.mediaUrl}
                </Typography>
              )}
              {r.voiceUrl && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  🎙️ Voice note: {r.voiceUrl}
                </Typography>
              )}
              {r.ctaLabel && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  🔗 CTA: {r.ctaLabel}{r.ctaUrl ? ` → ${r.ctaUrl}` : ''}
                </Typography>
              )}
            </Paper>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function AppointmentsPanel({
  notify, onSetupUser,
}: {
  notify: (m: string, s?: 'success' | 'error') => void;
  onSetupUser: (cea: string) => void;
}) {
  const [rows, setRows] = useState<AdminAppointment[] | null>(null);
  const [includePast, setIncludePast] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(() => {
    setRows(null);
    fetchAdminAppointments(includePast).then(setRows).catch(() => notify('Failed to load appointments', 'error'));
  }, [includePast, notify]);
  useEffect(() => { load(); }, [load]);

  const setAttended = async (id: number, attended: boolean) => {
    setRows((prev) => prev && prev.map((r) => (r.id === id ? { ...r, attended } : r)));
    try { await updateAppointment(id, { attended }); } catch { notify('Failed to update attendance', 'error'); load(); }
  };
  const saveRemarks = async (id: number, remarks: string) => {
    try { await updateAppointment(id, { remarks }); } catch { notify('Failed to save remarks', 'error'); }
  };

  const fmt = (iso: string) => new Date(iso).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={includePast} onChange={(e) => setIncludePast(e.target.checked)} />}
          label="Show past appointments"
        />
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={{ textTransform: 'none' }}>
          Add appointment
        </Button>
      </Box>

      {!rows ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <EmptyState text={includePast ? 'No appointments.' : 'No upcoming appointments.'} />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Appointment</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell align="center">Attended</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>{fmt(a.appointmentAt)}<br /><Typography variant="caption" color="text.secondary">{a.durationMinutes} min</Typography></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={getPhotoUrl(a.photo)} sx={{ width: 36, height: 36 }}>
                        {(a.agentName || a.cea || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        {a.agentName || '—'}
                        <Typography variant="caption" color="text.secondary" component="div">{a.cea}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{a.mobile || '—'}<br /><Typography variant="caption" color="text.secondary">{a.email || ''}</Typography></TableCell>
                  <TableCell align="center">
                    <Checkbox checked={!!a.attended} onChange={(e) => setAttended(a.id, e.target.checked)} />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      placeholder="Add remarks…"
                      defaultValue={a.remarks || ''}
                      onBlur={(e) => { if ((e.target.value || '') !== (a.remarks || '')) saveRemarks(a.id, e.target.value); }}
                      sx={{ minWidth: 160 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      {a.whatsappUrl && (
                        <Button
                          size="small"
                          component="a"
                          href={a.whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<WhatsAppIcon />}
                          sx={{ textTransform: 'none', color: '#128c7e' }}
                        >
                          WhatsApp reminder
                        </Button>
                      )}
                      <Button size="small" onClick={() => onSetupUser(a.cea)} sx={{ textTransform: 'none' }}>
                        Set up as user
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <AddAppointmentDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        notify={notify}
        onAdded={() => { setAddOpen(false); load(); }}
      />
    </Box>
  );
}

function AddAppointmentDialog({
  open, onClose, notify, onAdded,
}: {
  open: boolean;
  onClose: () => void;
  notify: (m: string, s?: 'success' | 'error') => void;
  onAdded: () => void;
}) {
  const [cea, setCea] = useState('');
  const [agentName, setAgentName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [when, setWhen] = useState('');
  const [saving, setSaving] = useState(false);
  const [lookupState, setLookupState] = useState<'idle' | 'loading' | 'found' | 'not-found' | 'error'>('idle');
  const lookupSequence = useRef(0);

  useEffect(() => {
    if (open) {
      setCea('');
      setAgentName('');
      setMobile('');
      setEmail('');
      setWhen('');
      setLookupState('idle');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const normalizedCea = cea.trim();
    if (normalizedCea.length < 3) {
      setLookupState('idle');
      return;
    }

    const sequence = ++lookupSequence.current;
    setLookupState('loading');
    const timer = window.setTimeout(() => {
      fetchAdminAgentProfile(normalizedCea)
        .then((profile) => {
          if (lookupSequence.current !== sequence) return;
          if (!profile) {
            setLookupState('not-found');
            return;
          }
          setAgentName(profile.name || '');
          setMobile(profile.mobile || '');
          setEmail(profile.email || '');
          setLookupState('found');
        })
        .catch(() => {
          if (lookupSequence.current === sequence) setLookupState('error');
        });
    }, 400);

    return () => window.clearTimeout(timer);
  }, [open, cea]);

  const handleCeaChange = (value: string) => {
    setCea(value.toUpperCase());
    setAgentName('');
    setMobile('');
    setEmail('');
    setLookupState('idle');
  };

  const save = async () => {
    if (!cea.trim() || !when) { notify('CEA and appointment time are required', 'error'); return; }
    setSaving(true);
    try {
      const result = await createAppointment({
        cea: cea.trim(),
        agentName: agentName.trim() || undefined,
        mobile: mobile.trim() || undefined,
        email: email.trim() || undefined,
        appointmentAt: new Date(when).toISOString(),
      });
      notify(
        result.emailSent
          ? 'Appointment added and confirmation email sent'
          : `Appointment added, but confirmation email was not sent${result.emailError ? `: ${result.emailError}` : ''}`,
        result.emailSent ? 'success' : 'error',
      );
      onAdded();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to add appointment', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add appointment</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="CEA Number"
            value={cea}
            onChange={(e) => handleCeaChange(e.target.value)}
            required
            autoFocus
            helperText={
              lookupState === 'found' ? 'Agent details loaded'
                : lookupState === 'not-found' ? 'No agent found; enter the details manually'
                  : lookupState === 'error' ? 'Unable to look up agent; enter the details manually'
                    : 'Enter a CEA number to load the agent details'
            }
            InputProps={{
              endAdornment: lookupState === 'loading'
                ? <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
                : undefined,
            }}
          />
          <TextField label="Agent name" value={agentName} onChange={(e) => setAgentName(e.target.value)} />
          <TextField label="Mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} />
          <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField
            label="Appointment date & time"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
        >
          {saving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
      <Typography color="text.secondary">{text}</Typography>
    </Paper>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Access Code Quotas Tab (admin sets how many codes each agent can generate)
// ─────────────────────────────────────────────────────────────────────────
function AccessCodeQuotasTab({ notify }: { notify: (msg: string, s?: 'success' | 'error') => void }) {
  const [quotas, setQuotas] = React.useState<AccessCodeQuota[]>([]);
  const [loading, setLoading] = React.useState(true);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<AccessCodeQuota | null>(null);
  const [ceaInput, setCeaInput] = React.useState('');
  const [quotaInput, setQuotaInput] = React.useState('1');
  const [noteInput, setNoteInput] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { setQuotas(await fetchAdminAccessCodeQuotas()); }
    catch { notify('Failed to load quotas', 'error'); }
    finally { setLoading(false); }
  }, [notify]);

  React.useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null); setCeaInput(''); setQuotaInput('1'); setNoteInput('');
    setDialogOpen(true);
  };
  const openEdit = (q: AccessCodeQuota) => {
    setEditing(q); setCeaInput(q.CEANumber); setQuotaInput(String(q.Quota)); setNoteInput(q.Note ?? '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const cea = ceaInput.trim().toUpperCase();
    const quota = parseInt(quotaInput, 10);
    if (!cea || isNaN(quota) || quota < 0) {
      notify('CEA number and a valid quota (≥0) are required', 'error'); return;
    }
    setSaving(true);
    try {
      await upsertAdminAccessCodeQuota(cea, quota, noteInput.trim() || undefined);
      notify(`Quota for ${cea} saved`, 'success');
      setDialogOpen(false);
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (q: AccessCodeQuota) => {
    if (!window.confirm(`Remove quota for ${q.CEANumber}?`)) return;
    try {
      await deleteAdminAccessCodeQuota(q.CEANumber);
      notify(`Quota for ${q.CEANumber} removed`, 'success');
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to delete', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKeyIcon sx={{ color: ORANGE }} />
          <Typography variant="h6" fontWeight={700}>Access Code Quotas</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
        >
          Assign Quota
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Set how many access codes each agent (by CEA) is allowed to generate.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : quotas.length === 0 ? (
        <EmptyState text="No quotas assigned yet. Click 'Assign Quota' to get started." />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>CEA Number</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Quota</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Granted By</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {quotas.map((q) => (
                <TableRow key={q.Id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{q.CEANumber}</TableCell>
                  <TableCell>{q.UserName ?? '–'}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={q.Quota}
                      size="small"
                      sx={{ fontWeight: 700, bgcolor: `${ORANGE}22`, color: ORANGE }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{q.Note ?? '–'}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{q.GrantedBy ?? '–'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(q)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => handleDelete(q)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle fontWeight={700}>{editing ? 'Edit Quota' : 'Assign Quota'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="CEA Number"
            value={ceaInput}
            onChange={(e) => setCeaInput(e.target.value.toUpperCase())}
            disabled={!!editing}
            fullWidth
            placeholder="e.g. R012345A"
          />
          <TextField
            label="Quota"
            type="number"
            inputProps={{ min: 0 }}
            value={quotaInput}
            onChange={(e) => setQuotaInput(e.target.value)}
            fullWidth
          />
          <TextField
            label="Note (optional)"
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value)}
            fullWidth
            placeholder="e.g. Approved by management"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// All Access Codes Tab (admin tracking view)
// ─────────────────────────────────────────────────────────────────────────
function AllAccessCodesTab() {
  const [codes, setCodes] = React.useState<AccessCodeWithNames[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState<string | null>(null);

  React.useEffect(() => {
    setLoading(true);
    fetchAdminAccessCodes()
      .then(setCodes)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const handleCopy = (code: string, id: string) => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const getStatus = (c: AccessCodeWithNames) => {
    if (c.ActivatedBy) return { label: 'Activated', color: '#22c55e' };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const exp = new Date(c.ExpiryDate); exp.setHours(0, 0, 0, 0);
    if (exp < today) return { label: 'Expired', color: '#ef4444' };
    return { label: 'Active', color: '#3b82f6' };
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <VpnKeyIcon sx={{ color: ORANGE }} />
        <Typography variant="h6" fontWeight={700}>All Access Codes</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        All access codes generated across all agents. Read-only tracking view.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : codes.length === 0 ? (
        <EmptyState text="No access codes have been generated yet." />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Generated By</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Expires</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Activated By</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Activated At</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.map((c) => {
                const status = getStatus(c);
                return (
                  <TableRow key={c.Id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}>{c.Code}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{c.GeneratedBy}</Typography>
                      {c.GeneratedByName && <Typography variant="caption" color="text.secondary">{c.GeneratedByName}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {new Date(c.ExpiryDate).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status.label}
                        size="small"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${status.color}22`, color: status.color }}
                      />
                    </TableCell>
                    <TableCell>
                      {c.ActivatedBy ? (
                        <>
                          <Typography variant="body2" fontWeight={700}>{c.ActivatedBy}</Typography>
                          {c.ActivatedByName && <Typography variant="caption" color="text.secondary">{c.ActivatedByName}</Typography>}
                        </>
                      ) : '–'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {c.ActivatedAt ? new Date(c.ActivatedAt).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' }) : '–'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={copied === c.Id ? 'Copied!' : 'Copy code'}>
                        <IconButton size="small" onClick={() => handleCopy(c.Code, c.Id)}>
                          <ContentCopyIcon fontSize="small" sx={{ color: copied === c.Id ? '#22c55e' : undefined }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
